// src/components/VisualSuggestionsTab.jsx - Visual Suggestions Tab
import React, { useState } from 'react';
import { Sparkles, Plus, Check, Settings, RefreshCw, X, Maximize2 } from 'lucide-react';
import ImageGenerator from './ImageGenerator';

const VisualSuggestionsTab = ({ analysis, apiKey, uploadedFileName }) => {
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [generatedImages, setGeneratedImages] = useState({});
  const [contextInput, setContextInput] = useState('');
  const [showContextModal, setShowContextModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [expandedImage, setExpandedImage] = useState(null);

  // Extract visual suggestions from analysis with detailed AI prompts
  const getVisualSuggestions = () => {
    if (!analysis) return [];

    const suggestions = [];
    const presentationType = analysis.presentationType?.primary || 'Business';
    const primaryStrategy = analysis.strategicDirection?.primaryStrategy || 'Professional communication';
    const audienceProfile = analysis.contextualGrounding?.audienceProfile || 'Business professionals';

    // Context string for prompts
    const contextString = contextInput ? ` ${contextInput}` : '';

    // From strategic direction
    if (analysis.designDirection) {
      if (analysis.designDirection.imagery?.recommended) {
        suggestions.push({
          id: 'hero-imagery',
          type: 'Hero Image',
          priority: 'high',
          title: 'Main Visual Identity',
          description: analysis.designDirection.imagery.recommended,
          placement: 'Cover slide or key slides',
          reasoning: 'Establish strong visual identity and brand presence',
          style: analysis.designDirection.imagery.style || 'Professional',
          colors: analysis.designDirection.colors?.primary || ['#2B1B4C', '#4EF4A8'],
          prompt: `Create a professional ${presentationType.toLowerCase()} presentation hero image. The image should convey ${primaryStrategy.toLowerCase()} and appeal to ${audienceProfile.toLowerCase()}. Style: modern, clean, and sophisticated with colors ${analysis.designDirection.colors?.primary?.join(', ') || '#2B1B4C, #4EF4A8'}. Include abstract geometric elements that suggest growth and innovation. No text overlay.${contextString}`
        });
      }

      if (analysis.designDirection.backgrounds?.recommended) {
        suggestions.push({
          id: 'background-pattern',
          type: 'Background Element',
          priority: 'medium',
          title: 'Background Enhancement',
          description: analysis.designDirection.backgrounds.recommended,
          placement: 'Throughout presentation',
          reasoning: 'Create visual cohesion and professional appearance',
          style: 'Subtle patterns or gradients',
          colors: analysis.designDirection.colors?.secondary || ['#F8F9FA'],
          prompt: `Design a subtle background pattern for ${presentationType.toLowerCase()} presentations. Create elegant, minimal geometric patterns or soft gradients in ${analysis.designDirection.colors?.secondary?.join(', ') || '#F8F9FA, #E5E7EB'}. The pattern should be very light and not interfere with text readability. Style: modern, professional, non-distracting.${contextString}`
        });
      }
    }

    // Smart suggestions based on presentation content
    const slides = analysis.slides || [];
    const hasDataSlides = slides.some(slide => slide.charts > 0 || slide.tables > 0);
    const hasTextHeavySlides = slides.some(slide => slide.text_content && slide.text_content.length > 200);

    if (hasDataSlides) {
      suggestions.push({
        id: 'data-visualization',
        type: 'Data Visual',
        priority: 'high',
        title: 'Data Visualization Support',
        description: 'Custom visual elements to enhance data presentation and make statistics more engaging',
        placement: 'Data-heavy slides with charts or tables',
        reasoning: 'Transform complex data into visually appealing and digestible graphics',
        style: 'Clean infographic style with data visualization elements',
        colors: analysis.designDirection?.colors?.primary || ['#4EF4A8', '#FF6666', '#2B1B4C'],
        prompt: `Create modern data visualization graphics for ${presentationType.toLowerCase()} presentations. Include elements like: abstract chart backgrounds, data point highlights, statistical icons, and growth arrows. Use colors: ${analysis.designDirection?.colors?.primary?.join(', ') || '#4EF4A8, #FF6666, #2B1B4C'}. Style: clean, professional, infographic-inspired. No specific numbers or text.${contextString}`
      });
    }

    if (hasTextHeavySlides) {
      suggestions.push({
        id: 'content-support',
        type: 'Content Visual',
        priority: 'medium',
        title: 'Text Content Support',
        description: 'Visual metaphors and illustrations to break up text-heavy slides',
        placement: 'Slides with extensive text content',
        reasoning: 'Improve visual interest and help audience retention',
        style: 'Conceptual illustrations and metaphoric visuals',
        colors: analysis.designDirection?.colors?.primary || ['#2B1B4C', '#4EF4A8'],
        prompt: `Design conceptual illustrations for ${presentationType.toLowerCase()} content. Create abstract visual metaphors representing: communication, strategy, growth, teamwork, innovation. Style: modern line art and geometric shapes in ${analysis.designDirection?.colors?.primary?.join(', ') || '#2B1B4C, #4EF4A8'}. Professional, minimal, and supportive of text content.${contextString}`
      });
    }

    // Always include a compelling title slide option
    suggestions.push({
      id: 'title-slide',
      type: 'Title Graphic',
      priority: 'high',
      title: 'Opening Slide Impact',
      description: 'A powerful visual that sets the tone and captures attention from the first slide',
      placement: 'Title slide',
      reasoning: 'Create immediate impact and establish presentation credibility',
      style: 'Bold, professional graphic that embodies the presentation theme',
      colors: analysis.designDirection?.colors?.primary || ['#2B1B4C', '#4EF4A8', '#FF6666'],
      prompt: `Create a striking title slide graphic for a ${presentationType.toLowerCase()} presentation about "${analysis.contextualGrounding?.identifiedObjective || primaryStrategy}". The image should be: professional, engaging, and convey expertise. Include abstract elements suggesting: success, innovation, forward-thinking. Colors: ${analysis.designDirection?.colors?.primary?.join(', ') || '#2B1B4C, #4EF4A8, #FF6666'}. No text, clean background space for title overlay.${contextString}`
    });

    // Section dividers if presentation has multiple sections
    if (slides.length > 5) {
      suggestions.push({
        id: 'section-dividers',
        type: 'Section Graphics',
        priority: 'medium',
        title: 'Section Transitions',
        description: 'Clean visual breaks between different sections of your presentation',
        placement: 'Section break slides',
        reasoning: 'Improve presentation flow and help audience navigate content',
        style: 'Minimalist geometric elements',
        colors: analysis.designDirection?.colors?.primary || ['#2B1B4C', '#E5E7EB'],
        prompt: `Design minimalist section divider graphics for ${presentationType.toLowerCase()} presentations. Create clean geometric shapes, lines, and abstract elements that can separate content sections. Use colors: ${analysis.designDirection?.colors?.primary?.join(', ') || '#2B1B4C, #E5E7EB'}. Style: modern, professional, spacious. Should work as subtle background elements.${contextString}`
      });
    }

    return suggestions.slice(0, 6); // Limit to 6 suggestions for better UX
  };

  const visualSuggestions = getVisualSuggestions();

  const handleGenerateImage = (suggestion) => {
    setSelectedSuggestion(suggestion);
  };

  const handleRefreshSuggestions = () => {
    setIsRefreshing(true);
    // Simulate refresh - in a real app you might call an API to get new suggestions
    setTimeout(() => {
      setIsRefreshing(false);
      setShowContextModal(false);
      // Clear generated images to show fresh suggestions
      setGeneratedImages({});
    }, 500);
  };

  const handleImageGenerated = (suggestionId, imageResult) => {
    setGeneratedImages(prev => ({
      ...prev,
      [suggestionId]: imageResult
    }));
    setSelectedSuggestion(null);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-50 text-red-700 border-red-200';
      case 'medium': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      default: return 'bg-green-50 text-green-700 border-green-200';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high': return '🔥';
      case 'medium': return '⚡';
      default: return '✨';
    }
  };

  if (!analysis) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Sparkles className="w-8 h-8 text-purple-600" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Analysis Available</h3>
        <p className="text-gray-600 mb-6">Upload and analyze a presentation first to see visual suggestions</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center relative">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Visual Suggestions</h2>
        <p className="text-lg text-gray-600 mb-4">
          AI-generated recommendations for visual elements that will enhance your presentation
        </p>
        {/* Add Context Button */}
        <button
          onClick={() => setShowContextModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm font-medium"
        >
          <Settings className="w-4 h-4" />
          Add Context
        </button>
      </div>

      {/* Suggestions Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {visualSuggestions.map((suggestion) => {
          const isGenerated = generatedImages[suggestion.id];
          const isGenerating = selectedSuggestion?.id === suggestion.id;

          return (
            <div key={suggestion.id} className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200">
              {/* Header */}
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Sparkles className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{suggestion.title}</h3>
                      <p className="text-xs text-gray-500">{suggestion.type}</p>
                    </div>
                  </div>
                  <div className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(suggestion.priority)}`}>
                    {getPriorityIcon(suggestion.priority)} {suggestion.priority}
                  </div>
                </div>
                <p className="text-sm text-gray-600">{suggestion.description}</p>
              </div>

              {/* Details */}
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 gap-3 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Placement:</span>
                    <p className="text-gray-600">{suggestion.placement}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Why this helps:</span>
                    <p className="text-gray-600">{suggestion.reasoning}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Style:</span>
                    <p className="text-gray-600">{suggestion.style}</p>
                  </div>
                  {suggestion.colors && suggestion.colors.length > 0 && (
                    <div>
                      <span className="font-medium text-gray-700">Recommended Colors:</span>
                      <div className="flex gap-2 mt-1">
                        {suggestion.colors.slice(0, 3).map((color, index) => (
                          <div
                            key={index}
                            className="w-6 h-6 rounded-full border-2 border-white shadow-sm"
                            style={{ backgroundColor: color }}
                            title={color}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Generated Image Preview */}
                {isGenerated && (
                  <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2 text-green-800 mb-2">
                      <Check className="w-4 h-4" />
                      <span className="text-sm font-medium">Generated</span>
                    </div>
                    {isGenerated.imageUrl && (
                      <div className="relative group cursor-pointer">
                        <img
                          src={isGenerated.imageUrl}
                          alt={suggestion.title}
                          className="w-full h-32 object-cover rounded border transition-transform group-hover:scale-105"
                          onClick={() => setExpandedImage({ url: isGenerated.imageUrl, title: suggestion.title })}
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all rounded flex items-center justify-center">
                          <Maximize2 className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Generate Button */}
                <button
                  onClick={() => handleGenerateImage(suggestion)}
                  disabled={!apiKey || isGenerating}
                  className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    isGenerated
                      ? 'bg-green-100 text-green-700 hover:bg-green-200'
                      : 'bg-purple-600 text-white hover:bg-purple-700'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {isGenerating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Generating...
                    </>
                  ) : isGenerated ? (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Regenerate
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Generate Visual
                    </>
                  )}
                </button>

                {!apiKey && (
                  <p className="text-xs text-gray-500 text-center">
                    API key required for generation
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Image Generator Modal */}
      {selectedSuggestion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">Generate {selectedSuggestion.title}</h3>
                  <p className="text-gray-600">{selectedSuggestion.type}</p>
                </div>
                <button
                  onClick={() => setSelectedSuggestion(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="p-6">
              <ImageGenerator
                imagesuggestion={{
                  purpose: selectedSuggestion.title,
                  description: selectedSuggestion.description,
                  prompt: selectedSuggestion.prompt || selectedSuggestion.description,
                  placement: selectedSuggestion.placement,
                  reasoning: selectedSuggestion.reasoning,
                  priority: selectedSuggestion.priority
                }}
                apiKey={apiKey}
                onImageGenerated={(result) => handleImageGenerated(selectedSuggestion.id, result)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Context Modal */}
      {showContextModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">Add Context</h3>
                  <p className="text-gray-600">Provide additional context to improve visual suggestions</p>
                </div>
                <button
                  onClick={() => setShowContextModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Context
                  </label>
                  <textarea
                    value={contextInput}
                    onChange={(e) => setContextInput(e.target.value)}
                    placeholder="E.g., This is for a tech startup pitch, focus on innovation and growth. The audience is venture capitalists. Use blue and green colors..."
                    className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Describe your brand, audience, industry, or specific visual preferences
                  </p>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <h4 className="text-sm font-medium text-blue-800 mb-2">💡 Tips for better suggestions:</h4>
                  <ul className="text-xs text-blue-700 space-y-1">
                    <li>• Mention your industry (tech, healthcare, finance, etc.)</li>
                    <li>• Describe your audience (executives, students, investors, etc.)</li>
                    <li>• Specify visual style preferences (modern, traditional, playful, etc.)</li>
                    <li>• Include brand colors or themes you want to emphasize</li>
                  </ul>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={handleRefreshSuggestions}
                    disabled={isRefreshing}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                  >
                    {isRefreshing ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Refreshing...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4" />
                        Refresh Suggestions
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setShowContextModal(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-purple-600">
              {visualSuggestions.filter(s => s.priority === 'high').length}
            </div>
            <div className="text-sm text-gray-600">High Priority</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-blue-600">
              {Object.keys(generatedImages).length}
            </div>
            <div className="text-sm text-gray-600">Generated</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-green-600">
              {visualSuggestions.length}
            </div>
            <div className="text-sm text-gray-600">Total Suggestions</div>
          </div>
        </div>
      </div>

      {/* Expanded Image Modal */}
      {expandedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={() => setExpandedImage(null)}
        >
          <div className="relative max-w-4xl max-h-full bg-white rounded-lg overflow-hidden">
            <button
              onClick={() => setExpandedImage(null)}
              className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="p-4">
              <h3 className="text-lg font-semibold mb-4">{expandedImage.title}</h3>
              <img
                src={expandedImage.url}
                alt={expandedImage.title}
                className="max-w-full max-h-[70vh] object-contain mx-auto"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisualSuggestionsTab;